import React from 'react';
import { Droppable } from 'react-beautiful-dnd';
import InfiniteScroll from 'react-infinite-scroll-component';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Loader from '@/components/common/Loader';
import ConstituentQueryTypeFields from '@/constants/constituents/ConstituentQueryTypeFields';
import LoaderSizes from '@/constants/ui/LoaderSizes';
import useLifecycleCards from '../lib/hooks/useLifecycleCards';
import AddConstituent from './AddConstituent';
import EmptyLifecycleLane from './EmptyLifecycleLane';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import LifecycleCard from './LifecycleCard';
import LifecycleColumnHeader from './LifecycleColumnHeader';

function LifecycleColumn({
  className,
  columnId,
  dataTestid,
  description,
  isDragDisabled,
  name,
  onCancelUpdate,
  onMenuClick,
  onRemove,
  onUpdate,
  query,
  setQuery,
  selectedItems,
  setExcludedItems,
  setSelectedItems,
}) {
  const [page, setPage] = React.useState(0);
  const { cards, filters, isLoading, total, updateCard } = useLifecycleCards({
    columnId,
    page,
  });
  const scrollContainer = React.useRef(null);
  React.useEffect(() => {
    setPage(0);
  }, [filters]);

  // TODO(jon): Swap out this infinite scroll method with useSWRInfinite.
  const onNextPage = React.useCallback(() => {
    setPage(page + 1);
  }, [page]);

  return (
    <Droppable droppableId={`column-${columnId}`}>
      {(provided) => (
        <div
          className={classNames(
            className,
            'flex flex-col rounded-lg border border-grey-300 bg-grey-100 pb-3',
          )}
          data-testid={dataTestid}
          ref={provided.innerRef}
        >
          <LifecycleColumnHeader
            className="mb-3 flex items-start border-b border-grey-300 p-3"
            columnId={columnId}
            columnDescription={description}
            columnTitle={name}
            constituents={cards}
            filters={{
              ...filters,
              columnId,
              queryType: ConstituentQueryTypeFields.V3,
            }}
            onCancelUpdate={onCancelUpdate}
            onRemove={onRemove}
            onUpdate={onUpdate}
            query={query}
            setQuery={setQuery}
            setSelectedItems={setSelectedItems}
            total={total}
          />
          <div className="flex-grow overflow-y-auto px-3" ref={scrollContainer}>
            {isLoading && <Loader isCentered className="h-full items-center" />}
            {!isLoading && !!cards.length && (
              <InfiniteScroll
                dataLength={cards.length}
                scrollableTarget={scrollContainer.current}
                className="overflow-visible"
                hasMore={cards.length < total}
                next={onNextPage}
                loader={
                  <Loader
                    isCentered
                    className="my-8 w-full"
                    size={LoaderSizes.SM}
                  />
                }
              >
                {cards.map((constituent, i) => (
                  <LifecycleCard
                    columnId={columnId}
                    constituent={constituent}
                    key={constituent.constituentId}
                    className={i ? 'mt-3' : ''}
                    isDragDisabled={isDragDisabled}
                    onMenuClick={onMenuClick}
                    updateCard={updateCard(constituent.constituentId)}
                    index={i}
                    selectedItems={selectedItems}
                    setExcludedItems={setExcludedItems}
                    setSelectedItems={setSelectedItems}
                  />
                ))}
                {columnId && (
                  <AddConstituent columnId={columnId} className="mt-6" />
                )}
              </InfiniteScroll>
            )}
            {!isLoading && !cards.length && (
              <>
                <EmptyLifecycleLane name={name}>
                  {description}
                </EmptyLifecycleLane>
                {columnId && (
                  <AddConstituent columnId={columnId} className="mt-6" />
                )}
              </>
            )}
          </div>
        </div>
      )}
    </Droppable>
  );
}

LifecycleColumn.propTypes = {
  className: PropTypes.string,
  columnId: PropTypes.string,
  dataTestid: PropTypes.string,
  description: PropTypes.string,
  isDragDisabled: PropTypes.bool,
  name: PropTypes.string.isRequired,
  onCancelUpdate: PropTypes.func,
  onMenuClick: PropTypes.func,
  onRemove: PropTypes.func,
  onUpdate: PropTypes.func,
  query: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setQuery: PropTypes.func.isRequired,
  selectedItems: PropTypes.arrayOf(PropTypes.string).isRequired,
  setExcludedItems: PropTypes.func.isRequired,
  setSelectedItems: PropTypes.func.isRequired,
};

LifecycleColumn.defaultProps = {
  className: '',
  columnId: null,
  dataTestid: '',
  description: '',
  isDragDisabled: true,
  onCancelUpdate: () => {},
  onMenuClick: () => {},
  onRemove: () => {},
  onUpdate: () => {},
};

export default LifecycleColumn;
